import type { WorkflowView } from '@/lib/api/generated/model';

export interface FirstRunStep {
  key: 'workflow' | 'publish' | 'connect' | 'task';
  /** Not needed for a first run; said so on the step (spec 128 FR-012). */
  optional?: boolean;
  title: string;
  detail: string;
  done: boolean;
  /** Where the step is done: a draft's builder, a board, or the list. */
  target:
    | { kind: 'workflows' }
    | { kind: 'library' }
    | { kind: 'connection' }
    | { kind: 'builder'; workflowKey: string }
    | { kind: 'board'; workflowKey: string };
}

/**
 * The three things a workspace needs before it does anything, in the order
 * they can be done (spec 099 FR-004, FR-005). Each step says whether it is done
 * from what the workspace already holds, and points where it is done.
 */
export function firstRunSteps(
  workflows: readonly WorkflowView[],
  taskCount: number,
  /** An enabled GitHub or GitLab connection exists; `undefined` hides the step. */
  hasCodeHost?: boolean,
): FirstRunStep[] {
  const draft = workflows.find((w) => w.status !== 'published');
  const published = workflows.find((w) => w.status === 'published');
  return [
    {
      key: 'workflow',
      title: 'Create a workflow',
      detail: 'The steps work moves through, and what runs at each one.',
      done: workflows.length > 0,
      // The Library holds ready workflows to install — the shortest way to a
      // first one (spec 128 FR-013), rather than the empty list.
      target: { kind: 'library' },
    },
    {
      key: 'publish',
      title: 'Publish it',
      detail:
        'A published workflow gets a board; a draft is still being drawn.',
      done: published !== undefined,
      target: draft
        ? { kind: 'builder', workflowKey: draft.key }
        : { kind: 'workflows' },
    },
    ...(hasCodeHost === undefined
      ? []
      : [
          {
            key: 'connect' as const,
            optional: true,
            title: 'Connect GitHub or GitLab',
            detail:
              'So agents can push branches and open merge requests from a run.',
            done: hasCodeHost,
            target: { kind: 'connection' as const },
          },
        ]),
    {
      key: 'task',
      title: 'Start a task',
      detail: 'The first card on the board, and the first run of an agent.',
      done: taskCount > 0,
      target: published
        ? { kind: 'board', workflowKey: published.key }
        : { kind: 'workflows' },
    },
  ];
}

/** Whether the path should stand in for the overview (FR-006). */
export function isFirstRun(
  workflows: readonly WorkflowView[],
  taskCount: number,
): boolean {
  return !workflows.some((w) => w.status === 'published') && taskCount === 0;
}
